// src/features/players/components/CreatePlayerModal.tsx

import { useCallback, useEffect } from 'react';
import { Modal, TextInput, Textarea, Select, Button, Group, Stack } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useController } from 'react-hook-form';
import { PLAYER_CONSTRAINTS } from '@/lib/constants/player';
import { useCreatePlayer, useCopyPlayer } from '../usePlayerMutations';
import type { PlayerViewData } from '@/lib/types/Player';

const createPlayerSchema = z.object({
  label: z
    .string()
    .min(PLAYER_CONSTRAINTS.LABEL_MIN, 'Name is required')
    .max(PLAYER_CONSTRAINTS.LABEL_MAX, `Name must be at most ${PLAYER_CONSTRAINTS.LABEL_MAX} characters`),
  descShort: z
    .string()
    .max(PLAYER_CONSTRAINTS.DESC_SHORT_MAX, `Description must be at most ${PLAYER_CONSTRAINTS.DESC_SHORT_MAX} characters`),
  copyFromPlayerId: z.string().nullable(),
});

type CreatePlayerFormData = z.infer<typeof createPlayerSchema>;

interface CreatePlayerModalProps {
  opened: boolean;
  onClose: () => void;
  players: PlayerViewData[];
  onSuccess: (player: PlayerViewData) => void;
}

export function CreatePlayerModal({ opened, onClose, players, onSuccess }: CreatePlayerModalProps) {
  const createMutation = useCreatePlayer();
  const copyMutation = useCopyPlayer();

  const { control, handleSubmit, reset, formState: { errors } } = useForm<CreatePlayerFormData>({
    resolver: zodResolver(createPlayerSchema),
    defaultValues: {
      label: '',
      descShort: '',
      copyFromPlayerId: null,
    },
  });

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      reset({ label: '', descShort: '', copyFromPlayerId: null });
    }
  }, [opened, reset]);

  const labelField = useController({ control, name: 'label' });
  const descField = useController({ control, name: 'descShort' });
  const copyFromField = useController({ control, name: 'copyFromPlayerId' });

  const isPending = createMutation.isPending || copyMutation.isPending;

  const playerOptions = players.map(p => ({
    value: String(p.core.playerId),
    label: p.core.label,
  }));

  const onSubmit = useCallback((data: CreatePlayerFormData) => {
    const req = {
      label: data.label,
      descShort: data.descShort || null,
    };

    if (data.copyFromPlayerId) {
      const playerId = Number(data.copyFromPlayerId);
      copyMutation.mutate(
        { playerId, req },
        {
          onSuccess: (player) => {
            onSuccess(player);
            onClose();
          },
        },
      );
    } else {
      createMutation.mutate(req, {
        onSuccess: (player) => {
          onSuccess(player);
          onClose();
        },
      });
    }
  }, [createMutation, copyMutation, onSuccess, onClose]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Create Player"
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="Enter player name"
            required
            maxLength={PLAYER_CONSTRAINTS.LABEL_MAX}
            description={`${labelField.field.value.length}/${PLAYER_CONSTRAINTS.LABEL_MAX}`}
            value={labelField.field.value}
            onChange={labelField.field.onChange}
            onBlur={labelField.field.onBlur}
            error={errors.label?.message}
          />

          <Textarea
            label="Description"
            placeholder="Optional short description"
            maxLength={PLAYER_CONSTRAINTS.DESC_SHORT_MAX}
            description={`${descField.field.value?.length ?? 0}/${PLAYER_CONSTRAINTS.DESC_SHORT_MAX}`}
            rows={2}
            value={descField.field.value}
            onChange={descField.field.onChange}
            onBlur={descField.field.onBlur}
            error={errors.descShort?.message}
          />

          {players.length > 0 && (
            <Select
              label="Copy from existing player"
              placeholder="Start fresh (no copy)"
              data={playerOptions}
              clearable
              searchable
              value={copyFromField.field.value}
              onChange={copyFromField.field.onChange}
            />
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={isPending}>
              {copyFromField.field.value ? 'Copy Player' : 'Create Player'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
